import type { DesktopPlatform } from './api';
import { useNamespaceCopy } from '@/i18n/ns';
import { entrySurfaceCopy } from '@/i18n/ns/entry-surfaces';

export const platformLabels: Record<DesktopPlatform, string> = {
  macos: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
};

export function PlatformIcon({ platform }: { platform: DesktopPlatform }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      {platform === 'macos' ? (
        <path d="M16.6 1.5c.2 1.5-.5 3-1.4 4-1 .9-2.3 1.5-3.6 1.3-.2-1.4.5-2.9 1.4-3.8 1-1 2.4-1.5 3.6-1.5ZM19.9 17.6c-.5 1.2-.8 1.7-1.5 2.7-.9 1.3-2.1 2.9-3.6 2.9-1.4 0-1.8-.9-3.7-.9s-2.4.9-3.8.9c-1.5 0-2.6-1.5-3.5-2.8C1.3 16.7.9 11.5 2.5 9c1.1-1.8 2.9-2.8 4.7-2.8 1.5 0 2.5 1 3.8 1 1.2 0 2-1 3.8-1 1.5 0 3.1.8 4.2 2.1-3.7 2-3.1 7.2.9 9.3Z" />
      ) : null}
      {platform === 'windows' ? (
        <path d="m2 4 9-1.2v8.3H2V4Zm10.5-1.4L22 1.3v9.8h-9.5V2.6ZM2 12.6h9V21l-9-1.2v-7.2Zm10.5 0H22v9.8l-9.5-1.3v-8.5Z" />
      ) : null}
      {platform === 'linux' ? (
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="4" width="20" height="16" rx="3" />
          <path d="m6 9 3 3-3 3m7 0h5" />
        </g>
      ) : null}
    </svg>
  );
}

export function PlatformPicker({
  selected,
  onSelect,
}: {
  selected: DesktopPlatform | null;
  onSelect: (platform: DesktopPlatform) => void;
}) {
  const copy = useNamespaceCopy(entrySurfaceCopy);
  const descriptions = { macos: copy.forMac, windows: copy.forPc, linux: copy.includingNix };
  return (
    <div className="desktop-platform-picker" role="group" aria-label={copy.choosePlatform}>
      {(['macos', 'windows', 'linux'] as const).map((platform) => (
        <button
          type="button"
          className="desktop-platform-option"
          key={platform}
          aria-pressed={selected === platform}
          onClick={() => onSelect(platform)}
        >
          <PlatformIcon platform={platform} />
          <span className="desktop-platform-label">{platformLabels[platform]}</span>
          <span className="desktop-platform-description">{descriptions[platform]}</span>
        </button>
      ))}
    </div>
  );
}
